import React from 'react';
import PropTypes from 'prop-types';
import {
  Layout,
  Button,
  Space,
  Divider,
  Typography,
  Empty,
  List,
  Modal,
  Input
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  ArrowRightOutlined,
  AudioOutlined,
  BarChartOutlined,
  FolderOpenOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SaveOutlined,
  MessageOutlined,
  SendOutlined,
  CloseOutlined
} from '@ant-design/icons';
import VoiceModeView from '../forms/VoiceModeView';
import CompactFormView from '../forms/CompactFormView';
import PPTXViewer from '../../../common/PPTXViewer';
import MarkdownRenderer from '../../../common/MarkdownRenderer';
import PDFViewerCore from '../../../common/PDFViewerCore';
import styles from './ReportFormFilling.module.css';

const { Text } = Typography;

/**
 * ReportFormFilling - Step 2 of wizard: Fill report content
 *
 * Orchestrates VoiceModeView and CompactFormView based on inputMode.
 * Includes resizable AI Assistant drawer for direct mode.
 *
 * Extracted from ReportWriterV2 to improve modularity
 */
const ReportFormFilling = ({
  // Report context
  selectedReport,
  inputMode,

  // Voice/Assistant mode props
  voiceSections,
  currentSection,
  sectionTranscripts,
  voiceTranscript,
  isRecording,

  // Form data
  formData,

  // UI state
  loading,
  activeOperation,
  drawerOpen,
  splitRatio,
  isDragging,

  // Chat state
  chatMessages,
  chatInput,
  sources,

  // Document viewer state
  documentViewerOpen,
  currentDocument,

  // Navigation
  onSetCurrentStep,

  // Input mode switching
  onSetInputMode,

  // Voice/Assistant handlers
  onGenerateReport,
  onUpdateFormData,
  onSetCurrentSection,
  onStartVoiceInput,
  onHandleVoiceToAI,
  onSetSectionTranscripts,
  onSetVoiceTranscript,
  onPreviousSection,
  onNextSection,

  // Direct mode handlers
  onEvaluateReport,
  onSearchArchive,
  onValidateRequiredFields,

  // Drawer handlers
  onSetDrawerOpen,
  onSplitterMouseDown,
  onSplitterMouseMove,
  onSplitterMouseUp,

  // Chat handlers
  onSetChatInput,
  onChatSubmit,
  onOpenDocument,

  // Document viewer handlers
  onSetDocumentViewerOpen,
  onSetCurrentDocument
}) => {
  return (
    <Layout className={styles.layout}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <Space className={styles.topBarSpace}>
          <Space>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => onSetCurrentStep(1)}>
              Change Mode
            </Button>
            <Divider type="vertical" />
            <Text strong>
              {selectedReport === 'knowledge_gap' ? 'Knowledge Gap' : 'Key Decision'} - {
                inputMode === 'voice' ? 'Voice Input' : inputMode === 'assistant' ? 'Report Assistant' : 'Direct Edit'
              }
            </Text>
          </Space>
          <Space>
            {(inputMode === 'voice' || inputMode === 'assistant') && (
              <>
                <Button size="small" onClick={() => onSetInputMode('direct')} icon={<EditOutlined />}>
                  Switch to Direct Edit
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<ArrowRightOutlined />}
                  onClick={onGenerateReport}
                  disabled={voiceSections.filter(s => sectionTranscripts[s.key]?.formatted).length === 0}
                >
                  Generate Report from {inputMode === 'assistant' ? 'Assistant' : 'Transcripts'}
                </Button>
              </>
            )}
            {inputMode === 'direct' && (
              <>
                <Button size="small" onClick={() => onSetInputMode('voice')} icon={<AudioOutlined />}>
                  Switch to Voice
                </Button>
                <Divider type="vertical" />
                <Button
                  size="small"
                  onClick={onEvaluateReport}
                  icon={<BarChartOutlined />}
                  type={activeOperation === 'evaluate' ? 'primary' : 'default'}
                  loading={loading && activeOperation === 'evaluate'}
                >
                  Evaluate Report
                </Button>
                <Button
                  size="small"
                  onClick={onSearchArchive}
                  icon={<FolderOpenOutlined />}
                  type={activeOperation === 'archive' ? 'primary' : 'default'}
                  loading={loading && activeOperation === 'archive'}
                >
                  Check Archive
                </Button>
                <Divider type="vertical" />
                <Button
                  size="small"
                  onClick={() => onSetDrawerOpen(!drawerOpen)}
                  icon={drawerOpen ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                >
                  {drawerOpen ? 'Hide' : 'Show'} Assistant
                </Button>
                <Divider type="vertical" />
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => {
                    if (onValidateRequiredFields()) {
                      onSetCurrentStep(3);
                    }
                  }}
                >
                  Continue to Finalize & Save
                </Button>
              </>
            )}
          </Space>
        </Space>
      </div>

      {/* Content */}
      <div className={styles.contentWrapper}>
        {(inputMode === 'voice' || inputMode === 'assistant') ? (
          <VoiceModeView
            voiceSections={voiceSections}
            currentSection={currentSection}
            sectionTranscripts={sectionTranscripts}
            formData={formData}
            inputMode={inputMode}
            voiceTranscript={voiceTranscript}
            isRecording={isRecording}
            loading={loading}
            onUpdateFormData={onUpdateFormData}
            onSetCurrentSection={onSetCurrentSection}
            onStartVoiceInput={onStartVoiceInput}
            onHandleVoiceToAI={onHandleVoiceToAI}
            onSetSectionTranscripts={onSetSectionTranscripts}
            onSetVoiceTranscript={onSetVoiceTranscript}
            onSetInputMode={onSetInputMode}
            onPreviousSection={onPreviousSection}
            onNextSection={onNextSection}
          />
        ) : (
          <div
            className={`${styles.directModeContainer} ${isDragging ? styles.directModeContainerDragging : ''}`}
            onMouseMove={onSplitterMouseMove}
            onMouseUp={onSplitterMouseUp}
          >
            {/* Left: Form */}
            <div
              className={`${styles.formArea} ${!isDragging ? styles.formAreaAnimated : ''}`}
              style={{ width: drawerOpen ? `${splitRatio}%` : '100%' }}
            >
              <CompactFormView
                selectedReport={selectedReport}
                formData={formData}
                onFormDataChange={onUpdateFormData}
              />
            </div>

            {/* Draggable Divider */}
            {drawerOpen && (
              <div
                onMouseDown={onSplitterMouseDown}
                className={isDragging ? styles.dividerActive : styles.divider}
                onMouseEnter={(e) => e.currentTarget.classList.add(styles.dividerActive)}
                onMouseLeave={(e) => !isDragging && e.currentTarget.classList.remove(styles.dividerActive)}
              />
            )}

            {/* Right: AI Assistant (embedded) */}
            {drawerOpen && (
              <div
                className={`${styles.assistantArea} ${!isDragging ? styles.assistantAreaAnimated : ''}`}
                style={{ width: `${100 - splitRatio}%` }}
              >
                <div className={styles.assistantWrapper}>
                  {/* AI Assistant Header */}
                  <div className={styles.assistantHeader}>
                    <Space className={styles.assistantHeaderSpace}>
                      <Space>
                        <MessageOutlined />
                        <Text strong>AI Assistant</Text>
                      </Space>
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => onSetDrawerOpen(false)}
                      >
                        Close
                      </Button>
                    </Space>
                  </div>

                  {/* Messages container - matches AI Coach pattern */}
                  <div className={styles.messagesContainer}>
                    {chatMessages.length === 0 ? (
                      <Empty
                        description="No messages"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Text type="secondary">Use the action buttons above to get AI assistance</Text>
                      </Empty>
                    ) : (
                      <>
                        {Array.isArray(chatMessages) && chatMessages.map((msg, idx) => (
                          <div
                            key={`${msg.role}-${idx}`}
                            className={`${styles.messageBubbleWrapper} ${msg.role === 'user' ? styles.messageBubbleWrapperUser : styles.messageBubbleWrapperAssistant}`}
                          >
                            <div className={`${styles.messageBubble} ${msg.role === 'user' ? styles.messageBubbleUser : styles.messageBubbleAssistant}`}>
                              <MarkdownRenderer content={msg.content} />

                              {/* Render clickable document links if present */}
                              {msg.documents && msg.documents.length > 0 && (
                                <div className={styles.documentLinksContainer}>
                                  {msg.documents.map((doc, docIdx) => (
                                    <div key={docIdx} className={styles.documentLinkItem}>
                                      <span className={styles.documentLinkBullet}>•</span>
                                      <Button
                                        type="link"
                                        size="small"
                                        onClick={() => onOpenDocument(doc.project_id, doc.document_id, doc.filename)}
                                        className={styles.documentLinkButton}
                                      >
                                        {doc.filename || 'Untitled Document'}
                                      </Button>
                                      <span className={styles.documentLinkDetails}>
                                        : {doc.description} ({doc.confidence}% match)
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Fixed input at bottom - matches AI Coach pattern */}
                  <div className={styles.inputContainer}>
                    <div className={styles.inputWrapper}>
                      <Input.TextArea
                        rows={3}
                        value={chatInput}
                        onChange={(e) => onSetChatInput(e.target.value)}
                        placeholder="Ask AI for help..."
                        disabled={loading}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onChatSubmit();
                          }
                        }}
                        className={styles.textarea}
                      />
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={onChatSubmit}
                        loading={loading}
                        disabled={!chatInput.trim()}
                        className={styles.sendButton}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      <Modal
        title={currentDocument?.filename}
        open={documentViewerOpen}
        destroyOnClose={true}
        onCancel={() => {
          onSetDocumentViewerOpen(false);
          onSetCurrentDocument(null);
        }}
        width="90%"
        className={styles.modalTop}
        footer={[
          <Button key="close" onClick={() => {
            onSetDocumentViewerOpen(false);
            onSetCurrentDocument(null);
          }}>
            Close
          </Button>
        ]}
      >
        {currentDocument && (
          <div className={styles.modalContent}>
            {currentDocument.filename?.toLowerCase().endsWith('.pdf') ? (
              <PDFViewerCore
                pdfUrl={currentDocument.url}
                filename={currentDocument.filename}
                showToolbar={true}
                toolbarPosition="top"
                containerStyle={{ height: '100%' }}
              />
            ) : (currentDocument.filename?.toLowerCase().endsWith('.pptx') ||
                currentDocument.filename?.toLowerCase().endsWith('.ppt')) ? (
              <PPTXViewer
                key={currentDocument.document_id}
                documentId={currentDocument.document_id}
                filename={currentDocument.filename}
                showToolbar={true}
                toolbarPosition="top"
                containerStyle={{ height: '100%' }}
              />
            ) : (
              <div className={styles.emptyPreview}>
                <Empty
                  description="Preview not available for this file type"
                  extra={
                    <Button
                      type="primary"
                      href={currentDocument.url}
                      download={currentDocument.filename}
                    >
                      Download to View
                    </Button>
                  }
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

ReportFormFilling.propTypes = {
  // Report context
  selectedReport: PropTypes.oneOf(['knowledge_gap', 'key_decision']).isRequired,
  inputMode: PropTypes.oneOf(['voice', 'assistant', 'direct']).isRequired,

  // Voice/Assistant mode props
  voiceSections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      number: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      supportText: PropTypes.string
    })
  ).isRequired,
  currentSection: PropTypes.string.isRequired,
  sectionTranscripts: PropTypes.objectOf(
    PropTypes.shape({
      raw: PropTypes.string,
      formatted: PropTypes.string,
      loading: PropTypes.bool
    })
  ).isRequired,
  voiceTranscript: PropTypes.string.isRequired,
  isRecording: PropTypes.bool.isRequired,

  // Form data
  formData: PropTypes.object.isRequired,

  // UI state
  loading: PropTypes.bool.isRequired,
  activeOperation: PropTypes.string,
  drawerOpen: PropTypes.bool.isRequired,
  splitRatio: PropTypes.number.isRequired,
  isDragging: PropTypes.bool.isRequired,

  // Chat state
  chatMessages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired
    })
  ).isRequired,
  chatInput: PropTypes.string.isRequired,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      project_id: PropTypes.string,
      document_id: PropTypes.string,
      filename: PropTypes.string.isRequired
    })
  ).isRequired,

  // Document viewer state
  documentViewerOpen: PropTypes.bool.isRequired,
  currentDocument: PropTypes.shape({
    filename: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }),

  // Navigation
  onSetCurrentStep: PropTypes.func.isRequired,

  // Input mode switching
  onSetInputMode: PropTypes.func.isRequired,

  // Voice/Assistant handlers
  onGenerateReport: PropTypes.func.isRequired,
  onUpdateFormData: PropTypes.func.isRequired,
  onSetCurrentSection: PropTypes.func.isRequired,
  onStartVoiceInput: PropTypes.func.isRequired,
  onHandleVoiceToAI: PropTypes.func.isRequired,
  onSetSectionTranscripts: PropTypes.func.isRequired,
  onSetVoiceTranscript: PropTypes.func.isRequired,
  onPreviousSection: PropTypes.func.isRequired,
  onNextSection: PropTypes.func.isRequired,

  // Direct mode handlers
  onEvaluateReport: PropTypes.func.isRequired,
  onSearchArchive: PropTypes.func.isRequired,
  onValidateRequiredFields: PropTypes.func.isRequired,

  // Drawer handlers
  onSetDrawerOpen: PropTypes.func.isRequired,
  onSplitterMouseDown: PropTypes.func.isRequired,
  onSplitterMouseMove: PropTypes.func.isRequired,
  onSplitterMouseUp: PropTypes.func.isRequired,

  // Chat handlers
  onSetChatInput: PropTypes.func.isRequired,
  onChatSubmit: PropTypes.func.isRequired,
  onOpenDocument: PropTypes.func.isRequired,

  // Document viewer handlers
  onSetDocumentViewerOpen: PropTypes.func.isRequired,
  onSetCurrentDocument: PropTypes.func.isRequired
};

export default ReportFormFilling;
